<template>
  <div class="crm-view">
    <div class="renewal-stats-container">
      <RenewalView
        :stats="renewalStats"
        @intervalChanged="onRenewalIntervalChanged"
      />
      <!-- <InteractionsView :stats="renewalStats" @intervalChanged="onInteractionIntervalChanged" /> -->
      <StatisticsView :stats="renewalStats" />
    </div>
    <CompanyListView
      v-if="crmStore.isRCOrEmulating"
      :engagementInterval="interactionInterval"
      :renewalInterval="renewalInterval"
    />
  </div>
</template>

<script setup lang="ts">
import RenewalView from "./RenewalView.vue";
import StatisticsView from "./StatisticsView.vue";
import CompanyListView from "./CompanyListView.vue";
import type { MiniCrmRenewal } from "@/Domain/MiniCrmRenewal";
import { onBeforeMount, ref, watch } from "vue";
import { useMainStore } from "@/stores/useMainStore";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useMiniCrmStore } from "@/stores/useMiniCrmStore";
import { UserRoles as Roles } from "@/Domain/UserRoles";

const main = useMainStore();
const crmStore = useMiniCrmStore();
crmStore.isRC = main.isInRole(Roles.ROLE_RC_PRIMARY) ?? false;

const renewalStats = ref<MiniCrmRenewal>();
const interactionInterval = ref<number>(-1);
const renewalInterval = ref<number>(-1);

const getRcRenewalDataAsync = async function () {
  if(!crmStore.isRCOrEmulating) return;

  return handleResponsePromiseAsync(
    api.get("GetRcRenewalData", {
      userLogin: main.userLogin,
      emulatedPersonJobId: crmStore.emulatedPersonJobId,
      ignoreYear: true,
    }),
    (data) => {
      // Delete the $id property from the response since it's breaking RenewalView
      delete (data.EngagementIntervals as { [key: number | string]: number })[
        "$id"
      ];
      delete (data.RenewalIntervals as { [key: number | string]: number })[
        "$id"
      ];
      renewalStats.value = data;
    },
    "MiniCrm.vue",
    "getRcRenewalDataAsync"
  );
};

const onInteractionIntervalChanged = (interval: number) => {
  interactionInterval.value = interval;
};

const onRenewalIntervalChanged = (interval: number) => {
  renewalInterval.value = interval;
};

watch(
  () => crmStore.emulatedPersonJobId,
  async () => {
    renewalStats.value = undefined;
    getRcRenewalDataAsync();
  }
);

onBeforeMount(() => {
  getRcRenewalDataAsync();
});
</script>

<style scoped lang="scss">
.crm-view {
  display: flex;
  gap: 16px;
  margin: 40px 20px;
  overflow: hidden;

  > .company-list {
    flex: 1;
    background: white;
    border-radius: 2px;
    box-shadow: 0px 0px 32px 0px rgba(0, 0, 0, 0.04);
  }
}

.renewal-stats-container {
  min-width: 340px;
  max-width: 340px;
  flex: 1;
}
</style>

<style lang="scss">
.crm-view {
  // Remove inner border in kendo date and time pickers
  .k-input-inner {
    border: unset !important;
  }
}
</style>
